import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import { generateUUID } from '@/lib/utils';
import { config } from '@/lib/config';

/**
 * API client configuration with interceptors for SPA authentication.
 *
 * Handles:
 * - XSRF token management (Sanctum)
 * - 401 Unauthenticated: Clear auth and redirect to login
 * - 419 CSRF Mismatch: Refresh token and retry
 * - 422 Validation: Pass errors to component
 * - Correlation ID header
 */

const API_BASE_URL = config.apiUrl;

/**
 * Create the API client instance.
 */
export const api: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true, // Send cookies for authentication
    withXSRFToken: true, // Auto-attach XSRF token
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

/**
 * Request interceptor to add correlation ID.
 */
api.interceptors.request.use(
    (config) => {
        // Add correlation ID for request tracing
        const correlationId = generateUUID();
        config.headers['X-Correlation-ID'] = correlationId;

        return config;
    },
    (error) => Promise.reject(error)
);

/**
 * Response interceptor to handle common error cases.
 */
api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config;

        // Check if request exists and hasn't been retried
        if (!originalRequest) {
            return Promise.reject(error);
        }

        const status = error.response?.status;

        // 401 Unauthenticated - Clear auth and redirect to login
        if (status === 401) {
            const authStore = useAuthStore();
            authStore.clearAuth();

            // Only redirect if not already on auth pages
            // Use soft navigation to avoid losing HMR state during development
            if (!window.location.pathname.startsWith('/login') &&
                !window.location.pathname.startsWith('/register') &&
                !window.location.pathname.startsWith('/forgot-password')) {
                // Dynamic import to avoid circular dependency
                import('@/router').then(({ router }) => {
                    router.push('/login');
                });
            }

            return Promise.reject(error);
        }

        // 419 CSRF Token Mismatch - Refresh token and retry
        if (status === 419) {
            // Check if already retried to prevent infinite loop
            if ((originalRequest as unknown as Record<string, boolean>)._retry) {
                return Promise.reject(error);
            }

            (originalRequest as unknown as Record<string, boolean>)._retry = true;

            try {
                // Refresh CSRF token
                await api.get('/sanctum/csrf-cookie');

                // Retry original request
                return api(originalRequest);
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }

        // 422 Validation Error - Let component handle it
        if (status === 422) {
            return Promise.reject(error);
        }

        // 429 Too Many Requests - Show rate limit message
        if (status === 429) {
            const uiStore = useUiStore();
            uiStore.showError(
                'Too Many Requests',
                'Please wait before making another request.'
            );
            return Promise.reject(error);
        }

        // 500+ Server Errors - Show generic error
        if (status && status >= 500) {
            const uiStore = useUiStore();
            uiStore.showError(
                'Server Error',
                'An unexpected error occurred. Please try again later.'
            );
        }

        return Promise.reject(error);
    }
);

/**
 * Refresh CSRF token from Sanctum.
 * Call this before login/register to ensure fresh token.
 */
export async function refreshCsrfToken(): Promise<void> {
    await api.get('/sanctum/csrf-cookie');
}

/**
 * Type-safe GET request.
 */
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await api.get<T>(url, { params });
    return response.data;
}

/**
 * Type-safe POST request.
 */
export async function post<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.post<T>(url, data);
    return response.data;
}

/**
 * Type-safe PUT request.
 */
export async function put<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.put<T>(url, data);
    return response.data;
}

/**
 * Type-safe PATCH request.
 */
export async function patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.patch<T>(url, data);
    return response.data;
}

/**
 * Type-safe DELETE request.
 */
export async function del<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.delete<T>(url, { data });
    return response.data;
}

/**
 * API Error helper type.
 */
export interface ApiError {
    message: string;
    error: string;
    errors?: Record<string, string[]>;
    correlation_id?: string;
}

/**
 * Check if error is an API error.
 */
export function isApiError(error: unknown): error is AxiosError<ApiError> {
    return axios.isAxiosError(error) && error.response?.data !== undefined;
}

/**
 * Extract validation errors from API error response.
 */
export function getValidationErrors(
    error: unknown
): Record<string, string[]> | null {
    if (isApiError(error) && error.response?.status === 422) {
        return error.response.data.errors || null;
    }
    return null;
}

/**
 * Extract error message from API error response.
 */
export function getErrorMessage(error: unknown): string {
    if (isApiError(error)) {
        return error.response?.data?.message || 'An error occurred.';
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'An unexpected error occurred.';
}

export default api;
